import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useOutletContext } from 'react-router-dom'
import Swal from 'sweetalert2'
import * as walletService from '../../services/walletService'
import {
  clearAffiliateMeCache,
  getAffiliateMe,
  getAffiliateCommissionHistory,
} from '../../services/affiliateService'
import { getWebSocketConnection } from '../../hooks/useSmartWebSocket'
import { getReferralDisplayPromotionConfig, type UserReferralDisplaySettings } from '../../services/promotionConfigService'
import type { AffiliateOutletContext } from './ModernAffiliateLayout'
import type { AffiliateMeData, AffiliateCommissionHistoryItem } from '../../services/affiliateService'
import { aggregateCommissionHistoryByDay } from '../../utils/affiliateHistoryUtils'
import {
  DEFAULT_MIN_COMMISSION_WITHDRAW,
  affiliateWithdrawShortfall,
  canAffiliateCommissionWithdraw,
  resolveMinCommissionWithdraw,
} from './affiliateConstants'

function formatHistoryDate(item: AffiliateCommissionHistoryItem): string {
  if (item.date) return item.date
  if (item.createdAt) {
    const d = new Date(item.createdAt)
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }).replace(/ /g, '-')
  }
  return '–'
}

const DEFAULT_DISPLAY_CONFIG: UserReferralDisplaySettings = {
  pageTitle: 'แนะนำเพื่อน',
  pageSubtitle: 'แชร์ลิงก์ของคุณเพื่อรับค่าคอมมิชชั่นจากยอดเล่นของสมาชิกที่คุณแนะนำ',
  withdrawButtonText: 'ถอนคอมมิชชั่น',
  minCommissionWithdrawalAmount: DEFAULT_MIN_COMMISSION_WITHDRAW,
  shareSectionTitle: 'ลิงก์แนะนำ',
  linkLabel: 'ลิงก์สำหรับโปรโมท',
  copyButtonText: 'คัดลอก',
  shareDescription:
    'ลิงก์ช่วยแชร์รับ {{rate}} ฟรี (แค่ก๊อปปี้ลิงก์ไปแชร์ก็ได้เงินแล้ว) ยิ่งแชร์มากยิ่งได้มาก ท่านสามารถนำลิงก์ด้านล่างนี้ไปแชร์ในช่องทางต่างๆ ไม่ว่าจะเป็น เว็บไซต์ส่วนตัว, Blog, Facebook หรือ Social Network อื่นๆ หากมีการสมัครสมาชิกโดยคลิกผ่านลิงก์ของท่านเข้ามา ลูกค้าที่สมัครเข้ามาก็จะอยู่ภายใต้เครือข่ายของท่านทันที และหากลูกค้าภายใต้เครือข่ายของท่านมีการเดิมพันเข้ามา ทุกยอดการเดิมพันท่านจะได้รับส่วนแบ่งในการแนะนำ {{rate}} ทันทีโดยไม่มีเงื่อนไข',
  disabledTitle: 'ปิดใช้งานชั่วคราว',
  disabledMessage: 'ระบบแนะนำเพื่อนปิดใช้งานในขณะนี้ กรุณาติดต่อแอดมินหากมีคำถาม',
  historyEmptyText: 'ยังไม่พบรายการ',
  hideZeroCommissionSegments: true,
  promotionRoute: '/affiliate',
  promotionCard: {
    imageUrl: '',
    imageAlt: 'แนะนำเพื่อน',
    badgeText: 'Referral',
    title: 'แนะนำเพื่อน',
    description: 'ดูคอมมิชชั่น ลิงก์โปรโมท และประวัติการแนะนำเพื่อน',
    buttonText: 'ไปหน้าแนะนำเพื่อน',
  },
}

function mergeDisplayConfig(input?: Partial<UserReferralDisplaySettings>): UserReferralDisplaySettings {
  return {
    ...DEFAULT_DISPLAY_CONFIG,
    ...(input || {}),
  }
}

function renderRateTemplate(text: string, rate: string): string {
  return String(text || '').replace(/\{\{\s*rate\s*\}\}/gi, rate || '0.00%')
}

function splitCommissionRate(rate: string): string[] {
  return String(rate || '')
    .split('/')
    .map((segment) => segment.trim())
    .filter(Boolean)
}

function isZeroCommissionSegment(segment: string): boolean {
  const match = String(segment || '').match(/(-?\d+(?:\.\d+)?)\s*%/)
  return match ? Number(match[1]) === 0 : false
}

const AffiliateOverviewPage: React.FC = () => {
  const { themeConfig } = useOutletContext<AffiliateOutletContext>()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [affiliateDisabled, setAffiliateDisabled] = useState(false)
  const [affiliateData, setAffiliateData] = useState<AffiliateMeData | null>(null)
  const [commissionHistory, setCommissionHistory] = useState<AffiliateCommissionHistoryItem[]>([])
  const [displayConfig, setDisplayConfig] = useState<UserReferralDisplaySettings>(DEFAULT_DISPLAY_CONFIG)
  const refreshDisplayConfigRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const refreshAffiliateRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const lastDisplayEventRef = useRef<{ key: string; at: number }>({ key: '', at: 0 })
  const lastAffiliateEventRef = useRef<{ key: string; at: number }>({ key: '', at: 0 })
  const socketProxySubscribedRef = useRef(false)

  const loadDisplayConfig = useCallback(async () => {
    try {
      const res = await getReferralDisplayPromotionConfig(themeConfig.tenantId)
      if (res?.success && res.data) {
        setDisplayConfig(mergeDisplayConfig(res.data.settings))
      } else {
        setDisplayConfig(DEFAULT_DISPLAY_CONFIG)
      }
    } catch {
      setDisplayConfig(DEFAULT_DISPLAY_CONFIG)
    }
  }, [themeConfig.tenantId])

  const loadAffiliateOverview = useCallback(async (options?: { silent?: boolean; force?: boolean }) => {
    if (!options?.silent) {
      setLoading(true)
    }
    setError(null)

    if (options?.force) {
      clearAffiliateMeCache()
    }

    try {
      const [me, history] = await Promise.all([
        getAffiliateMe(options?.force === true),
        getAffiliateCommissionHistory(100),
      ])

      setAffiliateDisabled(false)
      setAffiliateData(me)
      setCommissionHistory(history)
    } catch (e: unknown) {
      const err = e as { status?: number; message?: string; response?: { message?: string } }
      const status = err?.status
      const msg = (err?.response?.message || err?.message || '') as string
      const isDisabled = status === 404 || (msg && msg.toLowerCase().includes('not enabled'))

      if (isDisabled) {
        setAffiliateDisabled(true)
        setAffiliateData(null)
        setCommissionHistory([])

        if (!options?.silent) {
          Swal.fire({
            icon: 'info',
            title: displayConfig.disabledTitle,
            text: displayConfig.disabledMessage,
            confirmButtonText: 'ตกลง',
          })
        }
      } else {
        setError(err instanceof Error ? err.message : 'โหลดข้อมูลไม่สำเร็จ')
      }
    } finally {
      if (!options?.silent) {
        setLoading(false)
      }
    }
  }, [displayConfig.disabledMessage, displayConfig.disabledTitle])

  useEffect(() => {
    void loadDisplayConfig()
    void loadAffiliateOverview({ force: true })

    return () => {
      if (refreshAffiliateRef.current) {
        clearTimeout(refreshAffiliateRef.current)
        refreshAffiliateRef.current = null
      }
    }
  }, [loadAffiliateOverview, loadDisplayConfig])

  useEffect(() => {
    const handleDisplayConfigUpdate = (event: Event) => {
      const detail = (event as CustomEvent)?.detail as {
        contentType?: string
        type?: string
        promotionType?: string
        timestamp?: string
        updatedAt?: string
        content?: {
          type?: string
          promotionType?: string
          updatedAt?: string
        }
      } | undefined

      const isReferralDisplayUpdate =
        detail?.type === 'referral_display_config_updated' ||
        detail?.promotionType === 'referral_display' ||
        detail?.content?.type === 'referral_display_config_updated' ||
        detail?.content?.promotionType === 'referral_display' ||
        (detail?.contentType === 'promotion_config' && (
          detail?.promotionType === 'referral_display' ||
          detail?.content?.promotionType === 'referral_display'
        ))

      if (!isReferralDisplayUpdate) return

      const eventKey = [
        detail?.type || '',
        detail?.promotionType || '',
        detail?.contentType || '',
        detail?.content?.type || '',
        detail?.content?.promotionType || '',
        detail?.timestamp || detail?.updatedAt || detail?.content?.updatedAt || '',
      ].join('|')

      const now = Date.now()
      if (
        lastDisplayEventRef.current.key === eventKey &&
        now - lastDisplayEventRef.current.at < 1200
      ) {
        return
      }
      lastDisplayEventRef.current = { key: eventKey, at: now }

      if (refreshDisplayConfigRef.current) {
        clearTimeout(refreshDisplayConfigRef.current)
      }
      refreshDisplayConfigRef.current = setTimeout(() => {
        void loadDisplayConfig()
      }, 250)
    }

    window.addEventListener('content_update', handleDisplayConfigUpdate as EventListener)
    window.addEventListener('referral_display_config_updated', handleDisplayConfigUpdate as EventListener)

    return () => {
      if (refreshDisplayConfigRef.current) {
        clearTimeout(refreshDisplayConfigRef.current)
        refreshDisplayConfigRef.current = null
      }
      window.removeEventListener('content_update', handleDisplayConfigUpdate as EventListener)
      window.removeEventListener('referral_display_config_updated', handleDisplayConfigUpdate as EventListener)
    }
  }, [loadDisplayConfig])

  useEffect(() => {
    const handleAffiliateConfigUpdate = (event: Event) => {
      const detail = (event as CustomEvent)?.detail as {
        tenantId?: string
        enabled?: boolean
        timestamp?: string
      } | undefined

      const eventKey = [
        detail?.tenantId || '',
        String(detail?.enabled),
        detail?.timestamp || '',
      ].join('|')

      const now = Date.now()
      if (
        lastAffiliateEventRef.current.key === eventKey &&
        now - lastAffiliateEventRef.current.at < 1200
      ) {
        return
      }
      lastAffiliateEventRef.current = { key: eventKey, at: now }

      if (detail?.enabled === false) {
        setAffiliateDisabled(true)
        Swal.fire({
          icon: 'info',
          title: displayConfig.disabledTitle,
          text: displayConfig.disabledMessage,
          confirmButtonText: 'ตกลง',
        })
      } else if (detail?.enabled === true) {
        setAffiliateDisabled(false)
      }

      if (refreshAffiliateRef.current) {
        clearTimeout(refreshAffiliateRef.current)
      }
      refreshAffiliateRef.current = setTimeout(() => {
        void loadAffiliateOverview({ silent: true, force: true })
      }, 250)
    }

    window.addEventListener('affiliate_config_updated', handleAffiliateConfigUpdate as EventListener)
    return () => {
      if (refreshAffiliateRef.current) {
        clearTimeout(refreshAffiliateRef.current)
        refreshAffiliateRef.current = null
      }
      window.removeEventListener('affiliate_config_updated', handleAffiliateConfigUpdate as EventListener)
    }
  }, [displayConfig.disabledMessage, displayConfig.disabledTitle, loadAffiliateOverview])

  useEffect(() => {
    let cleanupSocket: (() => void) | undefined
    let intervalId: ReturnType<typeof setInterval> | undefined

    const subscribe = (socket: ReturnType<typeof getWebSocketConnection>) => {
      if (!socket || socketProxySubscribedRef.current) return
      socketProxySubscribedRef.current = true

      const forwardAffiliate = (data: {
        tenantId?: string
        enabled?: boolean
        updatedBy?: string
        timestamp?: string
        broadcastedAt?: string
      }) => {
        window.dispatchEvent(new CustomEvent('affiliate_config_updated', {
          detail: {
            tenantId: data?.tenantId,
            enabled: data?.enabled,
            updatedBy: data?.updatedBy,
            timestamp: data?.timestamp || data?.broadcastedAt,
          },
        }))
      }

      const forwardContent = (data: {
        type?: string
        tenantId?: string
        contentType?: string
        promotionType?: string
        timestamp?: string
        content?: {
          type?: string
          promotionType?: string
          updatedAt?: string
        }
      }) => {
        window.dispatchEvent(new CustomEvent('content_update', { detail: data }))
        if (typeof data?.type === 'string' && data.type.trim()) {
          window.dispatchEvent(new CustomEvent(data.type, { detail: data }))
        }
      }

      socket.on('affiliate_config_updated', forwardAffiliate)
      socket.on('content_update', forwardContent)

      return () => {
        socket.off('affiliate_config_updated', forwardAffiliate)
        socket.off('content_update', forwardContent)
        socketProxySubscribedRef.current = false
      }
    }

    const trySubscribe = () => {
      const socket = getWebSocketConnection()
      if (!socket) return
      if (intervalId) {
        clearInterval(intervalId)
        intervalId = undefined
      }
      cleanupSocket = subscribe(socket)
    }

    trySubscribe()
    if (!socketProxySubscribedRef.current) {
      intervalId = setInterval(trySubscribe, 1500)
    }

    return () => {
      if (intervalId) clearInterval(intervalId)
      cleanupSocket?.()
    }
  }, [])

  useEffect(() => {
    const refreshFromServer = () => {
      if (typeof document !== 'undefined' && document.visibilityState !== 'visible') {
        return
      }
      void loadDisplayConfig()
      void loadAffiliateOverview({ silent: true, force: true })
    }

    const intervalId = setInterval(refreshFromServer, 30000)
    window.addEventListener('focus', refreshFromServer)
    document.addEventListener('visibilitychange', refreshFromServer)

    return () => {
      clearInterval(intervalId)
      window.removeEventListener('focus', refreshFromServer)
      document.removeEventListener('visibilitychange', refreshFromServer)
    }
  }, [loadAffiliateOverview, loadDisplayConfig])

  const handleCopyLink = () => {
    if (affiliateDisabled || !affiliateData?.affiliateLink) return
    navigator.clipboard.writeText(affiliateData.affiliateLink)
    Swal.fire({
      icon: 'success',
      title: 'สำเร็จ!',
      text: 'คัดลอกลิงค์เรียบร้อยแล้ว',
      timer: 2000,
      showConfirmButton: false,
    })
  }

  const data: AffiliateMeData = affiliateData ?? {
    commissionRate: '0.00%',
    referredMembers: 0,
    totalEarnings: 0,
    remainingEarnings: 0,
    affiliateLink: '',
    affiliateCode: '',
  }
  const referralRate = data.commissionRate || '5%'
  const shareDescription = renderRateTemplate(displayConfig.shareDescription, referralRate)
  const commissionRateSegments = useMemo(() => {
    const segments = splitCommissionRate(data.commissionRate)
    const filteredSegments = displayConfig.hideZeroCommissionSegments
      ? segments.filter((segment) => !isZeroCommissionSegment(segment))
      : segments
    return filteredSegments.length > 0 ? filteredSegments : segments
  }, [data.commissionRate, displayConfig.hideZeroCommissionSegments])
  const remainingEarnings = Number(data.remainingEarnings || 0)
  const minCommissionWithdraw = resolveMinCommissionWithdraw(displayConfig.minCommissionWithdrawalAmount)
  const canWithdraw = !affiliateDisabled && canAffiliateCommissionWithdraw(remainingEarnings, minCommissionWithdraw)
  const withdrawShortfall = affiliateWithdrawShortfall(remainingEarnings, minCommissionWithdraw)
  const hasPartialCommission = !affiliateDisabled && remainingEarnings > 0 && !canWithdraw
  const dailyCommissionHistory = useMemo(
    () => aggregateCommissionHistoryByDay(commissionHistory),
    [commissionHistory],
  )

  if (loading) {
    return (
      <div className="htd-aff__loading">
        <div className="spinner-border" role="status" />
        <p>กำลังโหลดข้อมูล...</p>
      </div>
    )
  }
  if (error && !affiliateDisabled) {
    return (
      <div className="htd-aff__shell">
        <h3>โหลดข้อมูลไม่สำเร็จ</h3>
        <p>{error}</p>
      </div>
    )
  }

  const content = (
    <div className="htd-aff__stack">
      <section className="htd-aff__panel">
        <div className="htd-aff__hero-row">
          <div>
            <span className="htd-aff__pill mb-2">Affiliate Dashboard</span>
            <h2 className="mt-2 mb-1" style={{ color: '#fff', fontWeight: 800, fontSize: '1.1rem' }}>
              {displayConfig.pageTitle}
            </h2>
            <p className="mb-0" style={{ color: 'rgba(186,230,253,0.8)', fontSize: '0.86rem', maxWidth: 720 }}>
              {displayConfig.pageSubtitle}
            </p>
          </div>
          <div className="htd-aff__code-box">
            <span>ลิงก์แนะนำของคุณ</span>
            <strong>{data.affiliateCode || '-'}</strong>
          </div>
        </div>
      </section>

      <section className="htd-aff__panel">
        <div className="htd-aff__panel-head">
          <div>
            <h3>เรทคอมมิชชั่นที่ใช้งานอยู่</h3>
            <p>ส่วนแบ่งรายได้ตามการตั้งค่าปัจจุบัน</p>
          </div>
          <span className="htd-aff__pill">{commissionRateSegments.length} รายการ</span>
        </div>
        <div className="htd-aff__rate-list">
          {(commissionRateSegments.length > 0 ? commissionRateSegments : [data.commissionRate || '0.00%']).map((segment) => (
            <span key={segment} className="htd-aff__rate-chip">{segment}</span>
          ))}
        </div>
      </section>

      <section className="htd-aff__stats htd-aff__stats--3" aria-label="สรุปยอด">
        <div className="htd-aff__stat">
          <span>สมาชิกแนะนำ (คน)</span>
          <strong>{data.referredMembers}</strong>
        </div>
        <div className="htd-aff__stat">
          <span>รายได้ทั้งหมด (บาท)</span>
          <strong className="is-accent">{walletService.formatCurrency(data.totalEarnings)}</strong>
        </div>
        <div className="htd-aff__stat">
          <span>รายได้คงเหลือ (บาท)</span>
          <strong className="is-accent">{walletService.formatCurrency(data.remainingEarnings)}</strong>
        </div>
      </section>

      {hasPartialCommission ? (
        <Link to="../withdrawal" className="htd-aff__btn htd-aff__btn--warn">
          <div>
            <div style={{ fontWeight: 800, color: '#fff', marginBottom: 4 }}>
              <i className="fas fa-hourglass-half me-2" style={{ color: '#fbbf24' }} />
              {displayConfig.withdrawButtonText}
            </div>
            <div style={{ fontSize: '0.8rem', color: 'rgba(226,232,240,0.82)' }}>
              ยอดคงเหลือ {walletService.formatCurrency(remainingEarnings)} — ต้องสะสมอีก{' '}
              {walletService.formatCurrency(withdrawShortfall)} บาท (ขั้นต่ำ {minCommissionWithdraw} บาท)
            </div>
          </div>
          <i className="fas fa-chevron-right" />
        </Link>
      ) : (
        <Link
          to="../withdrawal"
          className={`htd-aff__btn${canWithdraw ? '' : ' is-disabled'}`}
          aria-disabled={!canWithdraw}
        >
          <i className="fas fa-dollar-sign" />
          {displayConfig.withdrawButtonText}
        </Link>
      )}

      <section className="htd-aff__panel htd-aff__panel--bright">
        <div className="htd-aff__panel-head">
          <div>
            <h3>{displayConfig.shareSectionTitle}</h3>
            <p>{displayConfig.linkLabel}</p>
          </div>
          <span className="htd-aff__pill">พร้อมแชร์ได้ทันที</span>
        </div>
        <div className="htd-aff__link-row">
          <input
            type="text"
            readOnly
            value={data.affiliateLink}
            className="htd-aff__input"
            aria-label="ลิงค์แนะนำ"
          />
          <button type="button" className="htd-aff__copy-btn" onClick={handleCopyLink}>
            <i className="fa fa-copy me-1" aria-hidden />
            {displayConfig.copyButtonText}
          </button>
        </div>
      </section>

      <section className="htd-aff__panel">
        <div className="htd-aff__panel-head">
          <div>
            <h3>ประวัติคอมมิชชั่นล่าสุด</h3>
            <p>สรุปรายวันจากสมาชิกในเครือข่าย</p>
          </div>
        </div>
        <div className="htd-aff__table-wrap">
          <table className="htd-aff__table htd-aff__table--stack">
            <thead>
              <tr>
                <th>วันที่</th>
                <th>ยอดเสียสมาชิก</th>
                <th>คอมมิชชั่น</th>
              </tr>
            </thead>
            <tbody>
              {dailyCommissionHistory.length === 0 ? (
                <tr>
                  <td colSpan={3} className="htd-aff__table-empty">{displayConfig.historyEmptyText}</td>
                </tr>
              ) : (
                dailyCommissionHistory.map((item, index) => (
                  <tr key={index}>
                    <td data-label="วันที่">{formatHistoryDate(item)}</td>
                    <td data-label="ยอดเสียสมาชิก">{walletService.formatCurrency(item.betAmount ?? 0)}</td>
                    <td className="is-gain" data-label="คอมมิชชั่น">{walletService.formatCurrency(item.commission ?? 0)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>

      <section className="htd-aff__note">
        <p>{shareDescription}</p>
      </section>
    </div>
  )

  if (affiliateDisabled) {
    return (
      <div className="position-relative">
        {content}
        <div className="htd-aff__disabled-veil">
          <span>{displayConfig.disabledTitle}</span>
        </div>
      </div>
    )
  }

  return content
}

export default AffiliateOverviewPage
